import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { Brand } from "./Brand";
import { useSession } from "../session";

const items = [
  { name: "Brands", icon: "fa-building", path: "/admin" },
  { name: "New brand", icon: "fa-circle-plus", path: "/admin/brands/new" },
  { name: "Add lead", icon: "fa-user-plus", path: "/admin/leads/new" },
  { name: "Performance", icon: "fa-gauge-high", path: "/admin/performance" },
  { name: "Delivery health", icon: "fa-heart-pulse", path: "/admin/delivery" },
  { name: "Audit log", icon: "fa-clipboard-list", path: "/admin/audit" },
  { name: "Platform settings", icon: "fa-sliders", path: "/admin/settings" },
];

const titles: Record<string, { title: string; sub: string }> = {
  "/admin": { title: "Brands", sub: "Every tenant on Leads Manager" },
  "/admin/performance": { title: "Client performance", sub: "Monthly lead targets" },
  "/admin/brands/new": { title: "New brand", sub: "Provision an isolated tenant" },
  "/admin/leads/new": { title: "Add lead", sub: "Create a lead for a client" },
  "/admin/delivery": { title: "Delivery health", sub: "Failures, paused destinations and re-sync" },
  "/admin/audit": { title: "Audit log", sub: "Access, deletions and config changes" },
  "/admin/settings": { title: "Platform settings", sub: "Defaults applied to every tenant" },
};

export function AdminLayout() {
  const { pathname, state } = useLocation();
  const normalisedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const meta = titles[normalisedPathname] ?? { title: "Admin", sub: "" };
  const notice =
    state && typeof state === "object" && "notice" in state && typeof state.notice === "string"
      ? state.notice
      : "";
  const session = useSession();
  if (session.loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-secondary">Loading…</div>
    );
  if (!session.authenticated) {
    const target =
      session.expiryTarget === "admin" ? "/admin/login?reason=session-expired" : "/admin/login";
    return <Navigate to={target} replace />;
  }
  if (!session.isFmcAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-light">
      <aside className="w-[268px] shrink-0 h-screen bg-primary flex flex-col">
        <div className="px-[20px] pt-[20px] pb-[16px] shrink-0">
          <div className="bg-surface-white rounded-md px-[18px] py-[16px]">
            <Brand wordmark="Admin Console" />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto scroll-thin px-[15px] pt-2">
          <div className="flex flex-col gap-0.5">
            {items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/admin"}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-[9px] rounded-xs transition-colors",
                    isActive
                      ? "bg-white/12 text-white"
                      : "text-white/65 hover:bg-white/8 hover:text-white",
                  ].join(" ")
                }
              >
                <i className={`fa-solid ${item.icon} text-[16px] w-[18px] text-center`} />
                <span className="text-body font-semibold">{item.name}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="shrink-0 border-t border-white/12 px-[15px] pt-3 pb-4 flex flex-col gap-0.5">
          <button
            onClick={session.exit}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xs text-white/65 hover:bg-white/8 hover:text-white transition-colors w-full"
          >
            <i className="fa-solid fa-right-from-bracket text-[15px] w-[18px] text-center" />
            <span className="text-body font-semibold">Log out</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <header className="shrink-0 h-[64px] bg-surface-white border-b border-default flex items-center justify-between px-[30px]">
          <div className="flex items-baseline gap-2.5 min-w-0">
            <h1 className="text-title-lg font-bold text-primary truncate">{meta.title}</h1>
            {meta.sub && <span className="text-body text-tertiary truncate">{meta.sub}</span>}
          </div>
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xs bg-surface-info text-brand-dark-teal text-micro font-semibold uppercase tracking-[0.06em]">
              {session.fmcAdminRole === "super_admin" ? "FMC Super Admin" : "FMC Admin"}
            </span>
            <div className="h-6 w-px bg-default" />
            <button className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-caption font-semibold">
                {session.userName
                  .split(" ")
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span className="text-small font-medium text-primary">{session.userName}</span>
              <i className="fa-solid fa-chevron-down text-[11px] text-tertiary" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto scroll-thin">
          <div className="px-[30px] py-[30px] max-w-[1240px]">
            {notice && (
              <div
                role="status"
                className="mb-5 flex items-center gap-2.5 rounded-md border border-status-green/30 bg-[rgba(41,166,118,0.1)] px-4 py-3 text-caption font-medium text-primary"
              >
                <i className="fa-solid fa-circle-check text-status-green" aria-hidden="true" />
                <span>{notice}</span>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
